import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import GridLayout, { setTransform } from "react-grid-layout";
import type { Compactor, EventCallback, Layout, LayoutItem as RGLLayoutItem } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Box, Button, Typography } from "@mui/material";
import type { DroppingItem } from "components/Report-generator/context/ReportGenerator-context";
import { TableExpandProvider } from "components/Report-generator/context/TableExpandContext";
import {
  cardToLayoutItem,
  compactDownOnly,
  DRAG_HANDLE_HEIGHT,
  gridItemWidth
} from "components/Report-generator/utils/gridUtils";
import type { LayoutItem } from "components/Report-generator/utils/gridUtils";
import { countKpiMetrics, getKpiMinGridH, KPI_EDIT_CHROME_PX } from "components/Report-generator/utils/kpiLayout";
import { useModals } from "hooks";
import type {
  AICardConfig,
  AICardData,
  CanvasCard,
  CardConfig,
  CardType,
  Currency,
  DateRangeValue,
  Filters,
  MacroBriefCardConfig,
  MacroBriefCardData,
  PageLayout,
  ReportMeta,
  ReportOrientation,
  ReportPage,
  SizePreset
} from "store/types";
import { GRID_CONSTANTS, PRESET_GRID_W } from "store/types/reports/report-generator";

import { CardWrapper } from "../CardWrapper";
import { ReportHeader } from "./ReportHeader";
import { SectionBreakHeader } from "./SectionBreakHeader";
import { TitlePageContent } from "./TitlePageContent";

const EMPTY_SET: Set<string> = new Set();

// Keeps a KPI card tall enough to fit its metrics while resizing: as the user
// narrows it (fewer columns → more rows needed), the height is forced up so the
// view never shrinks into a state that clips metrics.
type SizeConstraint = NonNullable<Layout[number]["constraints"]>[number];

// Resize-only guard: caps a card's width/height at the nearest neighbour's edge so a resize can
// never grow one card over another. Only constrains size (constrainSize) — dragging is unaffected,
// so cards still push each other when dragged.
const noOverlapConstraint: SizeConstraint = {
  name: "noOverlap",
  constrainSize: (item, w: number, h: number, _handle, context) => {
    let maxW = w;
    let maxH = h;
    for (const other of context.layout) {
      if (other.i === item.i) continue;
      // A card overlapping the (proposed) column span and sitting below caps how far DOWN this can grow.
      const sharesCols = other.x < item.x + w && other.x + other.w > item.x;
      if (sharesCols && other.y >= item.y + item.h) {
        maxH = Math.min(maxH, other.y - item.y);
      }
      // A card overlapping the (proposed) row span and sitting to the right caps how far RIGHT this can grow.
      const sharesRows = other.y < item.y + h && other.y + other.h > item.y;
      if (sharesRows && other.x >= item.x + item.w) {
        maxW = Math.min(maxW, other.x - item.x);
      }
    }
    return { w: Math.min(w, maxW), h: Math.min(h, maxH) };
  }
};

const kpiFitConstraint = (count: number): SizeConstraint => ({
  name: "kpiFit",
  constrainSize: (_item, w: number, h: number, _handle, context) => {
    const widthPercent = (w / context.cols) * 100;
    const minH = getKpiMinGridH(widthPercent, count, KPI_EDIT_CHROME_PX);
    return { w, h: Math.max(h, minH) };
  }
});

// Cards whose resize floor (both height and width) is measured from their rendered
// content (via the [data-text-content] node) rather than computed. Narrowing any of
// them reflows the content taller, so width is constrained too. Text/AI share the
// rich-text editor; Macro Brief joins them so it can't be compressed below its
// real content in either dimension.
const isMeasuredCard = (card: CanvasCard): boolean =>
  card.config.type === "text" || card.config.type === "ai_card" || card.config.type === "macroBrief";

// A key that changes whenever a sizable card's rendered content changes, so the
// measurement effect re-runs.
const textContentKey = (card: CanvasCard): string => {
  if (card.config.type === "text") return `t${(card.config as { content?: string }).content?.length ?? 0}`;
  if (card.config.type === "macroBrief") {
    const cfg = card.config as MacroBriefCardConfig;
    const data = card.data as MacroBriefCardData | undefined;
    const count = data?.indicators?.length ?? cfg.indicators.length;
    const narrativeLen = (cfg.showNarrative ? (data?.narrative ?? "") : "").length;
    return `m${count}:n${narrativeLen}:s${cfg.showNarrative ? 1 : 0}:l${card.loading ? 1 : 0}`;
  }
  const cfg = card.config as AICardConfig;
  const answer = cfg.answer ?? (card.data as AICardData | undefined)?.answer ?? "";
  return `q${cfg.question?.length ?? 0}:a${answer.length}:h${cfg.hideQuestion ? 1 : 0}:l${card.loading ? 1 : 0}`;
};

// Smallest grid height (rows) whose real react-grid-layout pixel height contains
// `needed` px. An h-row item is `h*CELL + (h-1)*GAP` tall.
const pxToMinGridH = (needed: number): number => {
  const cellPlusGap = GRID_CONSTANTS.GRID_CELL_HEIGHT + GRID_CONSTANTS.GAP;
  return Math.max(1, Math.ceil((needed + GRID_CONSTANTS.GAP) / cellPlusGap));
};

export const PAGE_LAYOUT_OPTIONS: { value: PageLayout; label: string; tag: string }[] = [
  { value: "title", label: "Title Page", tag: "TP" },
  { value: "header-content", label: "Header + Content", tag: "HC" },
  { value: "content", label: "Content Only", tag: "CO" },
  { value: "section", label: "Section Break", tag: "SB" }
];

interface PageViewProps {
  pageIndex: number;
  pageCount: number;
  /** Original page index matching card.page — used for layout/drop callbacks. */
  originalPageIndex: number;
  page: ReportPage;
  pageCards: CanvasCard[];
  allCards: CanvasCard[];
  pageWidth: number;
  pageHeight: number;
  selectedCardId: string;
  reportMeta: ReportMeta;
  /** Effective heading for this page (per-page override snapshot, falling back to reportMeta). */
  pageTitle: string;
  pageSubtitle: string;
  /** Report context shown in the header (kept identical between editor and preview). */
  period: DateRangeValue | null;
  currency: Currency;
  filters: Filters;
  orientation: ReportOrientation;
  isResizing: boolean;
  droppingItem: DroppingItem | null;
  isContinuation?: boolean;
  /** The table ends on this page — render as an editable grid (drop zone below the table). */
  isTableEnd?: boolean;
  /** Whether this page can be removed — true only for primary pages (not table continuation/spillover). */
  canRemovePage?: boolean;
  expandedRows?: Record<string, Set<string>>;
  onToggleExpand?: (cardId: string, rowKey: string) => void;
  cardOffsets?: Record<string, Record<string, number>>;
  originalCardIds?: Record<string, string>;
  onResizingChange: (resizing: boolean) => void;
  onUpdateMeta: (updates: Partial<ReportMeta>) => void;
  onLogoFileChange: (file: File | null) => void;
  onSelectCard: (id: string) => void;
  onRemoveCard: (id: string) => void;
  onDuplicateCard: (id: string) => void;
  onUpdateCard: (id: string, updates: Partial<Omit<CanvasCard, "id" | "type">>) => void;
  onPresetChange: (id: string, preset: SizePreset) => void;
  onRemovePage: () => void;
  onUpdatePage: (updates: Partial<ReportPage>) => void;
  onLayoutChange: (pageIndex: number, layout: LayoutItem[]) => void;
  onDropCard: (type: CardType, pageIndex: number, gridX: number, gridY: number, resolvedLayout?: LayoutItem[]) => void;
  onOpenSettings: (id: string) => void;
  zoom: number;
}
export const PageView: React.FC<PageViewProps> = ({
  pageIndex,
  pageCount,
  originalPageIndex,
  page,
  pageCards,
  allCards: _allCards,
  pageWidth,
  pageHeight,
  selectedCardId,
  reportMeta,
  pageTitle,
  pageSubtitle,
  period,
  currency,
  filters,
  orientation,
  isResizing: _isResizing,
  droppingItem,
  isContinuation: isContinuationPage,
  isTableEnd,
  canRemovePage = false,
  expandedRows,
  onToggleExpand,
  cardOffsets,
  originalCardIds,
  onResizingChange: _onResizingChange,
  onUpdateMeta,
  onLogoFileChange,
  onSelectCard,
  onRemoveCard,
  onDuplicateCard,
  onUpdateCard,
  onPresetChange,
  onRemovePage,
  onUpdatePage,
  onLayoutChange,
  onDropCard,
  onOpenSettings,
  zoom
}) => {
  const [showLayoutPicker, setShowLayoutPicker] = useState(false);
  const { confirmModal } = useModals();

  // Empty pages are removed immediately; pages with cards ask for confirmation first,
  // since removing them deletes every card on the page.
  const handleRemovePage = useCallback(() => {
    if (pageCards.length === 0) {
      onRemovePage();
      return;
    }
    confirmModal({
      title: "Remove page",
      msg: "This page has content. Removing it will delete all cards on it. Are you sure?",
      actionLabel: "Remove page",
      action: onRemovePage
    });
  }, [pageCards.length, onRemovePage, confirmModal]);

  // Switching a page to a Title Page hides (and drops) its cards, since a title page renders no
  // content — confirm first when the page still has cards. Other layouts keep the content grid.
  const handleChangeLayout = useCallback(
    (nextLayout: PageLayout) => {
      setShowLayoutPicker(false);
      if (nextLayout === "title" && pageCards.length > 0) {
        confirmModal({
          title: "Change to Title Page",
          msg: "A title page doesn't display content, so the components on this page will be deleted. Are you sure?",
          actionLabel: "Confirm",
          action: () => onUpdatePage({ layout: nextLayout })
        });
        return;
      }
      onUpdatePage({ layout: nextLayout });
    },
    [pageCards.length, onUpdatePage, confirmModal]
  );

  const layout = page.layout;
  const showHeader = layout === "header-content";
  const isTitlePage = layout === "title";
  const isSectionBreak = layout === "section";
  const readOnly = !!isContinuationPage;

  // A full page whose only content is a full-width table renders the table directly at
  // full height, bypassing react-grid-layout's 60px grid-row quantization. Table-end
  // pages are excluded: they keep the grid so the user gets a drop zone below the table.
  const directFillCard =
    !isTableEnd &&
    pageCards.length === 1 &&
    pageCards[0].config.type === "table" &&
    pageCards[0].gridW >= GRID_CONSTANTS.GRID_COLUMNS
      ? pageCards[0]
      : null;
  const directFillId = directFillCard ? (originalCardIds?.[directFillCard.id] ?? directFillCard.id) : "";

  // Full page width — horizontal padding is handled by GridLayout's containerPadding
  // so the grid's drop-sensitive area extends to the page edges.
  const contentWidth = pageWidth;
  // Reserve space for header (~120px) + footer (~40px) + padding (32px)
  const gridMinHeight = pageHeight - (showHeader ? 200 : 100);

  // Minimum grid height/width per text card, derived from the live-measured size
  // of its rich-text content. Forbids resizing a text card smaller than its text
  // (in either dimension) so the content can never be clipped by the container.
  const gridContentRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [textMinH, setTextMinH] = useState<Record<string, number>>({});
  const [textMinW, setTextMinW] = useState<Record<string, number>>({});

  // Re-measure whenever a text/AI card's content or size changes.
  const textSignature = useMemo(
    () =>
      pageCards
        .filter(isMeasuredCard)
        .map((c) => `${c.id}:${c.gridW}:${c.gridH}:${textContentKey(c)}`)
        .join(","),
    [pageCards]
  );

  useLayoutEffect(() => {
    const root = gridContentRef.current;
    if (!root) return;
    const measureRoot = measureRef.current;
    const nextH: Record<string, number> = {};
    const nextW: Record<string, number> = {};

    pageCards.forEach((card) => {
      if (!isMeasuredCard(card)) return;
      const cardEl = root.querySelector<HTMLElement>(`[data-card-id="${card.id}"]`);
      const textEl = cardEl?.querySelector<HTMLElement>("[data-text-content]");
      if (!textEl) return;

      // Height floor at the current width. offsetHeight is the unscaled layout
      // height (immune to the canvas zoom).
      nextH[card.id] = pxToMinGridH(DRAG_HANDLE_HEIGHT + textEl.offsetHeight);

      // Width floor: the narrowest grid width whose reflowed content still fits
      // the card's current height. The live node only exists at the current
      // width, so probe narrower widths on an off-screen clone (emotion classes
      // are copied, so it renders with identical styles).
      if (measureRoot) {
        const cardPx = card.gridH * GRID_CONSTANTS.GRID_CELL_HEIGHT + (card.gridH - 1) * GRID_CONSTANTS.GAP;
        const available = cardPx - DRAG_HANDLE_HEIGHT;
        const clone = textEl.cloneNode(true) as HTMLElement;
        clone.style.height = "auto";
        measureRoot.appendChild(clone);
        let minW = card.gridW;
        for (let w = 2; w < card.gridW; w++) {
          clone.style.width = `${gridItemWidth(pageWidth, w)}px`;
          if (clone.offsetHeight <= available) {
            minW = w;
            break;
          }
        }
        measureRoot.removeChild(clone);
        nextW[card.id] = minW;
      }
    });

    const sameNums = (prev: Record<string, number>, next: Record<string, number>) => {
      const keys = Object.keys(next);
      return keys.length === Object.keys(prev).length && keys.every((k) => prev[k] === next[k]);
    };
    setTextMinH((prev) => (sameNums(prev, nextH) ? prev : nextH));
    setTextMinW((prev) => (sameNums(prev, nextW) ? prev : nextW));
    // textSignature already encodes every text card's id/size/content, so it is
    // the precise trigger — depending on `pageCards` itself would re-measure on
    // every unrelated render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textSignature, pageWidth]);

  const gridLayout: Layout = useMemo(
    () =>
      pageCards.map((card) => {
        // A split table's continuation/end chunk is read-only and must stay put: mark it static so
        // dragging or dropping another card near it can never push the table (drops land below it).
        const isContinuationCard = originalCardIds?.[card.id] !== undefined;
        // Every card gets the no-overlap resize guard; KPI cards add their fit constraint before it
        // so the overlap cap always has the final say.
        const item: Layout[number] = {
          ...cardToLayoutItem(card),
          minW: 2,
          minH: 1,
          constraints: [noOverlapConstraint],
          ...(isContinuationCard ? { static: true } : {})
        };
        // Measured cards (text/AI/Macro Brief) can't be resized below their rendered
        // content in either dimension — narrowing reflows the content taller, so the
        // width floor is constrained alongside the height floor.
        if (isMeasuredCard(card)) {
          const minH = textMinH[card.id];
          if (minH) item.minH = Math.max(item.minH ?? 1, minH);
          const minW = textMinW[card.id];
          if (minW) item.minW = Math.max(item.minW ?? 2, minW);
        }
        // KPI cards can't be resized smaller than what fits their metrics.
        if (card.config.type === "kpi") {
          const count = countKpiMetrics(card);
          if (count > 0) {
            // Never narrower than the smallest preset, otherwise labels/values
            // wrap and break the layout.
            item.minW = PRESET_GRID_W.S;
            item.minH = getKpiMinGridH(card.widthPercent, count, KPI_EDIT_CHROME_PX);
            item.constraints = [kpiFitConstraint(count), noOverlapConstraint];
          }
        }
        return item;
      }),
    [pageCards, textMinH, textMinW, originalCardIds]
  );

  // Force GridLayout to reinitialize when card heights change.
  // RGL manages an internal layout state synced via useEffect + deepEqual, but
  // this sync can miss updates (e.g. nested table row expansion changes gridH
  // without changing the child key set). A key derived from heights forces a
  // fresh mount, guaranteeing the internal state matches the props.
  const gridLayoutKey = useMemo(() => pageCards.map((c) => c.gridH).join(","), [pageCards]);

  const rglDroppingItem: RGLLayoutItem | undefined = useMemo(() => {
    if (!droppingItem) return undefined;
    return { i: droppingItem.i, x: 0, y: 0, w: droppingItem.w, h: droppingItem.h };
  }, [droppingItem]);

  // Compute max grid rows that fit within the page's content area.
  const headerH = showHeader ? 120 : isSectionBreak ? 150 : 0;
  const containerPadV = 16 * 2;
  const availableH = pageHeight - headerH - containerPadV - GRID_CONSTANTS.FOOTER_HEIGHT;
  const cellPlusGap = GRID_CONSTANTS.GRID_CELL_HEIGHT + GRID_CONSTANTS.GAP;
  const maxRows = Math.floor((availableH + GRID_CONSTANTS.GAP) / cellPlusGap);

  // Stabilize config objects so RGL internals don't recompute on every render
  const gridConfig = useMemo(
    () => ({
      cols: GRID_CONSTANTS.GRID_COLUMNS,
      rowHeight: GRID_CONSTANTS.GRID_CELL_HEIGHT,
      margin: [GRID_CONSTANTS.GAP, GRID_CONSTANTS.GAP] as const,
      containerPadding: [28, 16] as const,
      maxRows
    }),
    [maxRows]
  );

  /** Compactor: vertical push logic for drag/drop, down-only compaction with maxRows boundary. */
  const pushCompactor: Compactor = useMemo(
    () => ({
      type: "vertical" as const,
      allowOverlap: false,
      compact(layout: Layout, _cols: number): Layout {
        return compactDownOnly(layout, maxRows);
      }
    }),
    [maxRows]
  );

  // Compensate for CSS zoom on the Canvas container. Setting `scale: zoom`
  // tells RGL to divide all coordinate calculations (drag deltas, start position,
  // drop coordinates) by the zoom factor so positions map correctly.
  // We intentionally omit `calcDragPosition` — the default path in RGL's
  // onDragStart correctly computes the element's position relative to its
  // offsetParent using `getBoundingClientRect() / transformScale`. A custom
  // `calcDragPosition` would lose the parent-relative offset, causing the
  // dragged item to jump on the first frame.
  const positionStrategy = useMemo(
    () => ({
      type: "transform" as const,
      scale: zoom,
      calcStyle(pos: { left: number; top: number; width: number; height: number }) {
        return setTransform(pos);
      }
    }),
    [zoom]
  );

  const dragConfig = useMemo(
    () => ({
      enabled: !readOnly,
      handle: ".rgl-drag-handle",
      bounded: false,
      threshold: 3
    }),
    [readOnly]
  );

  const resizeConfig = useMemo(() => ({ enabled: !readOnly, handles: ["se"] as const }), [readOnly]);

  const dropConfig = useMemo(
    () => ({
      enabled: !readOnly,
      defaultItem: { w: rglDroppingItem?.w ?? 6, h: rglDroppingItem?.h ?? 3 }
    }),
    [readOnly, rglDroppingItem?.w, rglDroppingItem?.h]
  );

  const persistLayout = useCallback(
    (newLayout: Layout) => {
      const items: LayoutItem[] = newLayout.map((item) => ({
        i: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h
      }));
      onLayoutChange(originalPageIndex, items);
    },
    [onLayoutChange, originalPageIndex]
  );

  const handleLayoutChange = useCallback((_newLayout: Layout) => {}, []);

  const handleDragStart: EventCallback = useCallback(() => {}, []);

  const handleDragStop: EventCallback = useCallback(
    (finalLayout: Layout) => {
      const clamped = finalLayout.map((item) => {
        if (item.y + item.h > maxRows) {
          return { ...item, y: Math.max(0, maxRows - item.h) };
        }
        return item;
      });
      persistLayout(clamped);
    },
    [persistLayout, maxRows]
  );

  const handleResizeStop: EventCallback = useCallback(
    (finalLayout: Layout) => {
      const clamped = finalLayout.map((item) => {
        if (item.y + item.h > maxRows) {
          return { ...item, h: Math.max(1, maxRows - item.y) };
        }
        return item;
      });
      persistLayout(clamped);
    },
    [persistLayout, maxRows]
  );

  const handleDrop = useCallback(
    (resolvedLayout: Layout, droppedItem: RGLLayoutItem | undefined, event: Event) => {
      if (!droppedItem) return;

      const cardType = (event as DragEvent).dataTransfer?.getData("cardType") as CardType | undefined;
      if (!cardType) return;

      // Resolve the drop against the existing cards (e.g. the table on a table-end page) by pushing
      // the new card DOWN so it can never land on top of another card — the same no-overlap rule the
      // grid uses. Reject the drop if the pushed-down card would then run off the page.
      const others: LayoutItem[] = resolvedLayout
        .filter((item) => item.i !== droppedItem.i)
        .map((item) => ({ i: item.i, x: item.x, y: item.y, w: item.w, h: item.h, static: item.static }));
      const dropped: LayoutItem = {
        i: droppedItem.i,
        x: droppedItem.x,
        y: droppedItem.y,
        w: droppedItem.w,
        h: droppedItem.h
      };
      const resolved = compactDownOnly([...others, dropped], maxRows);
      const placed = resolved.find((item) => item.i === droppedItem.i);
      if (!placed || resolved.some((item) => item.y + item.h > maxRows)) return;

      const existingLayout: LayoutItem[] = resolved.filter((item) => item.i !== droppedItem.i);
      onDropCard(cardType, originalPageIndex, placed.x, placed.y, existingLayout);
    },
    [onDropCard, originalPageIndex, maxRows]
  );

  return (
    <Box sx={{ position: "relative" }}>
      {/* Page badge + layout picker */}
      <Box
        sx={{
          position: "absolute",
          bottom: "100%",
          left: 0,
          right: 0,
          mb: "10px",
          display: "flex",
          alignItems: "center",
          gap: "10px"
        }}
      >
        <Typography sx={{ color: "#496682", fontSize: "16px", lineHeight: "16px" }}>
          Page {pageIndex + 1} of {pageCount}
          {isContinuationPage && " (continued)"}
        </Typography>
        {!isContinuationPage && (
          <Box
            onClick={() => setShowLayoutPicker((v) => !v)}
            sx={{
              bgcolor: showLayoutPicker ? "#071f35" : "#F6F8FC",
              color: showLayoutPicker ? "#FFFFFF" : "#071f35",
              border: "none",
              borderRadius: "999px",
              fontSize: "12px",
              lineHeight: "16px",
              height: "24px",
              p: "0 12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "2px"
            }}
          >
            {PAGE_LAYOUT_OPTIONS.find((o) => o.value === layout)?.label ?? layout}
            {showLayoutPicker ? <ArrowDropUpIcon sx={{ fontSize: 16 }} /> : <ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          </Box>
        )}
        {canRemovePage && pageCount > 1 && !isContinuationPage && (
          <Button
            onClick={handleRemovePage}
            startIcon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{
              ml: "auto",
              height: "24px",
              minHeight: 0,
              p: "0 12px",
              bgcolor: "#FFFFFF",
              color: "#071F35",
              fontSize: "12px",
              lineHeight: "16px",
              textTransform: "none",
              flexShrink: 0,
              "&:hover": { bgcolor: "#f6f8fc" }
            }}
          >
            Remove page
          </Button>
        )}
      </Box>

      {showLayoutPicker && !isContinuationPage && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 0,
            bgcolor: "#FFFFFF",
            borderRadius: "10px",
            boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
            border: "1px solid #dbe8f5",
            p: 1,
            display: "flex",
            gap: "6px",
            zIndex: 20,
            flexWrap: "wrap",
            justifyContent: "flex-start",
            width: Math.min(pageWidth - 32, 540)
          }}
        >
          {PAGE_LAYOUT_OPTIONS.map((option) => (
            <Box
              key={option.value}
              component="button"
              onClick={() => handleChangeLayout(option.value)}
              sx={{
                p: "8px 12px",
                border: `1px solid ${option.value === layout ? "#071f35" : "#dbe8f5"}`,
                borderRadius: "8px",
                bgcolor: option.value === layout ? "#E8F5F0" : "#FAFCFE",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
                minWidth: 94
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#071f35" }}>{option.tag}</Typography>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#071f35" }}>{option.label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          width: pageWidth,
          height: pageHeight,
          flexShrink: 0,
          borderRadius: "8px",
          bgcolor: "#FFFFFF",
          boxShadow: "0 4px 32px rgba(0,0,0,0.18), 0 1px 6px rgba(0,0,0,0.10)",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          // Shared background image applies to every non-title page (a white scrim over the image
          // emulates opacity while keeping page content fully readable above it). It takes precedence
          // over the editing grid lines, which otherwise show only on interactive content pages.
          ...(!isTitlePage && reportMeta.background
            ? {
                backgroundImage: `linear-gradient(rgba(255,255,255,${1 - (reportMeta.backgroundOpacity ?? 100) / 100}), rgba(255,255,255,${1 - (reportMeta.backgroundOpacity ?? 100) / 100})), url(${reportMeta.background})`,
                // Stretch both the scrim and the image to fill the whole page (no zoom/crop, no gaps).
                backgroundSize: "100% 100%",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat"
              }
            : isTitlePage || isSectionBreak || isContinuationPage
              ? {}
              : {
                  backgroundImage: [
                    "linear-gradient(rgba(7,31,53,0.05) 1px, transparent 1px)",
                    "linear-gradient(90deg, rgba(7,31,53,0.05) 1px, transparent 1px)"
                  ].join(", "),
                  backgroundSize: `${(contentWidth + GRID_CONSTANTS.GAP) / GRID_CONSTANTS.GRID_COLUMNS}px ${GRID_CONSTANTS.GRID_CELL_HEIGHT + GRID_CONSTANTS.GAP}px`
                })
        }}
        onClick={() => {
          onSelectCard("");
          setShowLayoutPicker(false);
        }}
      >
        {isTitlePage && (
          <TitlePageContent
            meta={reportMeta}
            title={pageTitle}
            subtitle={pageSubtitle}
            pageHeight={pageHeight}
            onTitleChange={(title) => onUpdatePage({ title })}
            onSubtitleChange={(subtitle) => onUpdatePage({ subtitle })}
            onUpdate={onUpdateMeta}
            onLogoFileChange={onLogoFileChange}
          />
        )}
        {isSectionBreak && (
          <SectionBreakHeader title={page.title ?? ""} onTitleChange={(title) => onUpdatePage({ title })} />
        )}
        {showHeader && (
          <ReportHeader
            meta={reportMeta}
            title={pageTitle}
            subtitle={pageSubtitle}
            period={period}
            currency={currency}
            filters={filters}
            onTitleChange={(title) => onUpdatePage({ title })}
            onSubtitleChange={(subtitle) => onUpdatePage({ subtitle })}
            onUpdate={onUpdateMeta}
            onLogoFileChange={onLogoFileChange}
          />
        )}

        {!isTitlePage && directFillCard && (
          // The table hugs its content (it never exceeds the page budget), so on a
          // full page it nearly fills the area and on the last page it ends right
          // after the final row. 32px top + 77px bottom mirrors the preview's
          // margin + footer area. overflow:hidden lives on the padded container
          // (clips at the page edge, not the card) so the card's border stays visible.
          <Box sx={{ flex: 1, minHeight: 0, position: "relative", overflow: "hidden", p: "32px 28px 77px" }}>
            <Box onClick={(e) => e.stopPropagation()}>
              <TableExpandProvider
                value={
                  expandedRows && onToggleExpand
                    ? {
                        expanded: expandedRows[directFillId] ?? EMPTY_SET,
                        onToggle: (rowKey: string) => onToggleExpand(directFillId, rowKey),
                        indexOffsets: cardOffsets?.[directFillCard.id]
                      }
                    : null
                }
              >
                <CardWrapper
                  card={directFillCard}
                  isSelected={selectedCardId === directFillId}
                  onSelect={() => onSelectCard(directFillId)}
                  onRemove={() => onRemoveCard(directFillId)}
                  onDuplicate={() => onDuplicateCard(directFillId)}
                  onOpenSettings={() => onOpenSettings(directFillId)}
                  onUpdateConfig={(config: CardConfig) => onUpdateCard(directFillId, { config })}
                  onPresetChange={(preset: SizePreset) => onPresetChange(directFillId, preset)}
                  // Primary page shows the card handle (label + controls) like any other card;
                  // continuation segments stay read-only display artifacts.
                  readOnly={readOnly}
                  orientation={orientation}
                />
              </TableExpandProvider>
            </Box>
          </Box>
        )}

        {!isTitlePage && !directFillCard && (
          <Box
            ref={gridContentRef}
            sx={{
              flex: 1,
              minHeight: 0,
              position: "relative",
              // Only show drop placeholder on interactive pages
              ...(!isContinuationPage && {
                "& .react-grid-item.react-grid-placeholder": {
                  background: "#1A3C34",
                  borderRadius: "6px",
                  opacity: 0.12
                }
              })
            }}
          >
            {/* Off-screen sandbox used to measure text content reflow at narrower widths. */}
            <Box
              ref={measureRef}
              aria-hidden
              sx={{
                position: "absolute",
                top: 0,
                left: -100000,
                visibility: "hidden",
                pointerEvents: "none",
                height: 0,
                overflow: "hidden"
              }}
            />
            <GridLayout
              key={gridLayoutKey}
              layout={gridLayout}
              gridConfig={gridConfig}
              compactor={pushCompactor}
              positionStrategy={positionStrategy}
              dropConfig={dropConfig}
              dragConfig={dragConfig}
              resizeConfig={resizeConfig}
              droppingItem={isContinuationPage ? undefined : rglDroppingItem}
              width={contentWidth}
              onLayoutChange={handleLayoutChange}
              onDragStart={handleDragStart}
              onDragStop={handleDragStop}
              onResizeStop={handleResizeStop}
              onDrop={handleDrop}
              autoSize
              style={{ minHeight: gridMinHeight, width: contentWidth }}
            >
              {pageCards.map((card) => {
                const origId = originalCardIds?.[card.id] ?? card.id;
                const isContinuationCard = originalCardIds?.[card.id] !== undefined;
                const expandValue =
                  expandedRows && onToggleExpand
                    ? {
                        expanded: expandedRows[origId] ?? EMPTY_SET,
                        onToggle: (rowKey: string) => onToggleExpand(origId, rowKey),
                        indexOffsets: cardOffsets?.[card.id]
                      }
                    : null;
                return (
                  <div key={card.id} data-card-id={card.id} onClick={(e) => e.stopPropagation()}>
                    <TableExpandProvider value={expandValue}>
                      <CardWrapper
                        card={card}
                        isSelected={selectedCardId === origId}
                        onSelect={() => onSelectCard(origId)}
                        onRemove={() => onRemoveCard(origId)}
                        onDuplicate={() => onDuplicateCard(origId)}
                        onOpenSettings={() => onOpenSettings(origId)}
                        onUpdateConfig={(config: CardConfig) => onUpdateCard(origId, { config })}
                        onPresetChange={(preset: SizePreset) => onPresetChange(origId, preset)}
                        readOnly={isContinuationCard}
                        orientation={orientation}
                      />
                    </TableExpandProvider>
                  </div>
                );
              })}
            </GridLayout>
            {/* Page content boundary line */}
            {!isContinuationPage && pageCards.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: 16 + maxRows * (GRID_CONSTANTS.GRID_CELL_HEIGHT + GRID_CONSTANTS.GAP) - GRID_CONSTANTS.GAP,
                  left: 28,
                  right: 28,
                  pointerEvents: "none",
                  zIndex: 10
                }}
              />
            )}
            {pageCards.length === 0 && !isContinuationPage && (
              <Box
                sx={{
                  position: "absolute",
                  top: "16px",
                  left: "28px",
                  right: "28px",
                  bottom: "47px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                  border: "2px dashed rgba(7,31,53,0.2)",
                  borderRadius: "10px",
                  boxSizing: "border-box",
                  pointerEvents: "none"
                }}
              >
                <svg width="32" height="40" viewBox="0 0 32 40" fill="none">
                  <rect x="1" y="1" width="30" height="38" rx="2" stroke="rgba(7,31,53,0.28)" strokeWidth="1.5" />
                  <line
                    x1="7"
                    y1="12"
                    x2="25"
                    y2="12"
                    stroke="rgba(7,31,53,0.28)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="7"
                    y1="18"
                    x2="25"
                    y2="18"
                    stroke="rgba(7,31,53,0.28)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="7"
                    y1="24"
                    x2="18"
                    y2="24"
                    stroke="rgba(7,31,53,0.28)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#92a7bc" }}>Drag components here</Typography>
                <Typography sx={{ fontSize: 12, color: "#92a7bc" }}>
                  {PAGE_LAYOUT_OPTIONS.find((o) => o.value === layout)?.label ?? "Page"} - Page {pageIndex + 1}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Footer */}
        <Box
          sx={{
            borderTop: "1px solid #dbe8f5",
            p: "12px 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexShrink: 0,
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0
          }}
        >
          <Typography sx={{ fontSize: 10, color: "#92a7bc" }}>
            Page {pageIndex + 1} of {pageCount}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
